import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { AppState } from 'reducers/reducers';
import { push } from 'connected-react-router';
import { trackEvent } from 'utils/analytics';
import { selectedSpotInfo } from 'selectors';
import useFilters from 'hooks/useFilters';
import { InsightsPeriod, TenantInsightsPeriod, TrendRentalType, trendRentalType } from 'types/PropertyInsights';
import Loader from 'components/Loader/Loader';
import { ReviewsRating, TrendPeriod } from 'components/PropertyInsights/helpers';
import DashboardContent from 'components/Dashboard/DashboardContent/DashboardContent';
import DriverTrendsWidget from 'components/PropertyInsights/DriverTrendsWidget/DriverTrendsWidget';
import DriverReviewsWidget from 'components/PropertyInsights/DriverReviewsWidget/DriverReviewsWidget';
import PeakTimesWidget from 'components/PropertyInsights/PeakTimesWidget/PeakTimesWidget';
import DriverDestinations from 'components/PropertyInsights/DriverDestinations/DriverDestinations';
import TransientDriversWidget from 'components/PropertyInsights/TransientDriversWidget/TransientDriversWidget';
import TenantUsageWidget from 'components/PropertyInsights/TenantUsageWidget/TenantUsageWidget';
import styles from './PropertyInsightsContainer.module.css';

type PropertyInsightsContainerProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

function insightsPeriodValidator(current: string | null): InsightsPeriod {
    return current && Object.values(InsightsPeriod).includes(current as InsightsPeriod)
        ? (current as InsightsPeriod)
        : InsightsPeriod.THIRTY_DAYS;
}

function PropertyInsightsContainer({ spot, loadingSpots, push }: PropertyInsightsContainerProps) {
    const { filters, urlUpdater } = useFilters<{
        trendRentalType: TrendRentalType;
        trendPeriod: TrendPeriod;
        reviewsPeriod: InsightsPeriod;
        rating: ReviewsRating;
        transientDriversPeriod: InsightsPeriod;
        tenantUsagePeriod: TenantInsightsPeriod;
        selectedTenants: string;
        peakTimesPeriod: InsightsPeriod;
    }>({
        filterValidators: {
            trendRentalType(current) {
                if (!current) return '';
                const rentalTypes = current.split(',').map((rt) => {
                    return Object.values(trendRentalType).includes(rt as TrendRentalType) ? rt : null;
                });
                return rentalTypes.filter((rt) => rt !== null).join(',');
            },
            trendPeriod(current) {
                return current && Object.values(TrendPeriod).includes(current as TrendPeriod)
                    ? current
                    : TrendPeriod.TWELVE;
            },
            reviewsPeriod: insightsPeriodValidator,
            rating(current) {
                return current && Object.values(ReviewsRating).includes(current as ReviewsRating)
                    ? current
                    : ReviewsRating.ALL;
            },
            transientDriversPeriod: insightsPeriodValidator,

            tenantUsagePeriod(current) {
                return current && Object.values(TenantInsightsPeriod).includes(current as TenantInsightsPeriod)
                    ? current
                    : TenantInsightsPeriod.THREE_MONTHS;
            },
            selectedTenants(current) {
                if (!current) return '';
                const tenants = current.split(',').map((t) => parseInt(t));
                return tenants.filter((t) => !isNaN(t)).join(',');
            },
            peakTimesPeriod: insightsPeriodValidator,
        },
        push,
    });

    if (!spot || loadingSpots) return <Loader />;

    useEffect(() => {
        trackEvent('Viewed Property Insights Page', { spot: spot.pk, spotName: spot.name });
    }, [spot]);

    return (
        <DashboardContent title="Property Insights">
            <DriverTrendsWidget spot={spot} filters={filters} urlUpdater={urlUpdater} />
            <div className={styles.rowWrapper}>
                <div className={styles.rowContainer}>
                    <DriverReviewsWidget spot={spot} filters={filters} urlUpdater={urlUpdater} />
                    <PeakTimesWidget spot={spot} filters={filters} urlUpdater={urlUpdater} />
                </div>
            </div>
            <div className={styles.rowWrapper}>
                <div className={styles.rowContainer}>
                    <DriverDestinations spot={spot} />
                    <TransientDriversWidget spot={spot} filters={filters} urlUpdater={urlUpdater} />
                </div>
            </div>
            {spot.spot_config.show_property_insights_tenant_widgets && (
                <TenantUsageWidget spot={spot} filters={filters} urlUpdater={urlUpdater} />
            )}
        </DashboardContent>
    );
}

function mapStateToProps(state: AppState) {
    return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        spot: selectedSpotInfo(state)!,
        loadingSpots: state.spots.orgSpotsLoading,
    };
}

const mapDispatchToProps = {
    push: push,
};

export default connect(mapStateToProps, mapDispatchToProps)(PropertyInsightsContainer);
